import { useState, useEffect } from "react";

export function useLocalStorageCache<T>({
  queryKey,
  queryFn,
  cacheDuration,
}: {
  queryKey: string | readonly unknown[];
  queryFn: () => Promise<T>;
  cacheDuration: number; // Cache duration in milliseconds
}): { data: T | undefined; isLoading: boolean; error: Error | null } {
  const [data, setData] = useState<T | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const cachedData = localStorage.getItem(JSON.stringify(queryKey));
        const cachedTimestamp = localStorage.getItem(
          `${JSON.stringify(queryKey)}_timestamp`,
        );

        if (cachedData && cachedTimestamp) {
          const parsedData = JSON.parse(cachedData);
          const timestamp = Number(cachedTimestamp);

          if (Date.now() - timestamp < cacheDuration) {
            setData(parsedData);
            setIsLoading(false);
            return;
          }
        }

        const fetchedData = await queryFn();
        setData(fetchedData);
        localStorage.setItem(
          JSON.stringify(queryKey),
          JSON.stringify(fetchedData),
        );
        localStorage.setItem(
          `${JSON.stringify(queryKey)}_timestamp`,
          Date.now().toString(),
        );
      } catch (err) {
        console.warn(err);
        setError(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [queryKey]); // Remove queryFn from the dependency array to avoid re-rendering

  return { data, isLoading, error };
}
